<script setup lang="ts">
import { onMounted, ref, watch  } from '#imports';
import type {PropType} from '#imports';
import iconArrowDown from '~/assets/image/contest/icon-arrow-down.svg';
import CitySelect from '~/components/CitySelect.vue';
import HTSelectGrade from '~/components/HTSelectGrade.vue';
import HTSelectSchool from '~/components/HTSelectSchool.vue';

const visible = defineModel('open', {
  type: Boolean,
  default: false,
});
const formData = defineModel('value', {
  type: Object,
  default: () => ({}),
});

defineProps({
  title: {
    type: String,
    default: '完善参赛信息',
  },
  desc: {
    type: String,
    default: '用于提供更完善的赛事服务，信息仅对比赛组织者可见',
  },
  okText: {
    type: String,
    default: '确认报名',
  },
  preloading: {
    type: Boolean,
    default: false,
  },
  formBtnDisabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  handleOnValidate: {
    type: Function as PropType<() => void>,
    default: () => {},
  },
  checkIsFormItemVisible: {
    type: Function as PropType<(itemValue: string) => boolean>,
    default: (itemValue: string) => {
        return true;
    },
  },
});
const emit = defineEmits(['finish']);

</script>

<template>
  <a-modal
    v-model:open="visible"
    :centered="true"
    wrap-class-name=""
    :width="468"
    :footer="null"
    :closable="true"
    :mask-closable="false"
  >
    <div class="mx-auto flex flex-col items-center justify-center">
      <h6 class="text-center text-base font-bold text-colorText">
        {{ title }}
      </h6>
      <div
        class="mt-2 w-full text-center text-sm leading-[22px] text-colorTextTertiary"
      >
        {{ desc }}
      </div>
      <div v-if="preloading">
        <a-spin>
          <div class="p-5">处理中...</div>
        </a-spin>
      </div>
      <div v-else class="mx-[26px] w-[368px]">
        <a-form
          class="!mt-5"
          :colon="false"
          :model="formData"
          :label-col="{
            style: {
              paddingTop: '4px',
              paddingBottom: '4px',
              paddingRight: '8px',
            },
          }"
          @finish="emit('finish')"
          @validate="handleOnValidate"
        >
          <a-form-item
            v-if="checkIsFormItemVisible('realName')"
            class="!mb-4"
            label="姓名"
            name="realName"
            :rules="[{ required: true, message: '', type: 'string' }]"
          >
            <a-input
              v-model:value.trim="formData.realName"
              class="!h-10"
              show-count
              :maxlength="15"
              allow-clear
              placeholder="请输入您的姓名"
              size="large"
            />
          </a-form-item>

          <a-form-item
            v-if="checkIsFormItemVisible('city')"
            class="!mb-4"
            label="省市"
            name="city"
            :rules="[{ required: true, message: '', type: 'string' }]"
          >
            <CitySelect
              v-model:city="formData.city"
              v-model:province="formData.province"
              placeholder="请选择省市信息"
              :allow-clear="false"
              class="city-select !rounded-sm"
            >
              <template #suffixIcon>
                <img :src="iconArrowDown" />
              </template>
            </CitySelect>
          </a-form-item>
          <a-form-item
            v-if="checkIsFormItemVisible('schoolName')"
            class="!mb-4"
            label="学校"
            name="schoolName"
            :rules="[{ required: true, message: '', type: 'string' }]"
          >
            <HTSelectSchool
              v-model:value="formData.schoolName"
              custom-class="school-select"
              size="large"
              width="100%"
              height="40px"
              placeholder="请输入您的学校"
            ></HTSelectSchool>
          </a-form-item>

          <a-form-item
            v-if="checkIsFormItemVisible('realGrade')"
            class="!mb-4"
            label="年级"
            name="realGrade"
            :rules="[{ required: true, message: '', type: 'number' }]"
          >
            <HTSelectGrade
              v-model:value.nonezero="formData.realGrade"
              width="100%"
              custom-class="grade-select"
            >
              <template #suffixIcon>
                <img :src="iconArrowDown" />
              </template>
            </HTSelectGrade>
          </a-form-item>
          <div class="flex justify-center pb-2">
            <a-button
              class="disabled-opacity mt-4 !h-8 w-[110px] !rounded !py-0 !text-sm font-bold"
              type="primary"
              size="large"
              html-type="submit"
              :loading="loading"
              :disabled="formBtnDisabled"
              >{{ okText }}</a-button
            >
          </div>
        </a-form>
      </div>
    </div>
  </a-modal>
</template>

<style scoped>
:deep(.city-select .ant-select-selector) {
  @apply !h-10 !rounded !pl-[11px];
}
:deep(.city-select .ant-select-selector .ant-select-selection-item) {
  @apply !h-[38px] border-none !text-sm !font-bold !leading-[38px] !text-colorText;
}
:deep(.city-select .ant-select-selection-placeholder) {
  @apply !h-[38px] !text-sm !leading-[38px] !text-colorTextQuaternary;
}

:deep(.school-select .ant-popover-inner-content) {
  @apply !p-0;
}

:deep(.grade-select .ant-select-selector) {
  @apply !h-10 !rounded !pl-[11px];
}
:deep(.grade-select .ant-select-selector .ant-select-selection-item) {
  @apply !h-[30px] border-none !text-sm !font-bold !leading-[30px] !text-colorText;
}
:deep(.grade-select .ant-select-selection-placeholder) {
  @apply !h-[30px] !text-sm  !leading-[30px] !text-colorTextQuaternary;
}

:deep(.ant-form-item-control-input .ant-input.ant-input-lg) {
  @apply !text-sm !font-bold !text-colorText;
}
:deep(.ant-form-item-control-input .ant-input-show-count-suffix) {
  @apply !text-sm;
}
:deep(.ant-form-item-control-input .ant-input.ant-input-lg::placeholder) {
  @apply !text-sm !font-normal !text-colorTextQuaternary;
}

:deep(label.ant-form-item-required.ant-form-item-no-colon) {
  @apply font-bold text-colorTextSecondary;
}
</style>
